/**
 * Date utilities for Timeline navigation
 */

import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import isoWeek from 'dayjs/plugin/isoWeek'

import {
    TimelineDateRange,
    WeekRangeOption,
    WEEK_RANGE_OPTIONS,
} from '../../../../types/timeline'

// Extend dayjs with week plugins
dayjs.extend(weekOfYear)
dayjs.extend(isoWeek)

/**
 * Get default date range (2 weeks starting from today)
 */
export const getDefaultDateRange = (): TimelineDateRange => {
    const start = dayjs().startOf('week') // Start from Monday
    const end = start.add(13, 'day') // 2 weeks

    return {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
    }
}

/**
 * Get date range based on week range option
 */
export const getDateRangeForOption = (
    option: WeekRangeOption,
    baseDate?: string
): TimelineDateRange => {
    const base = baseDate ? dayjs(baseDate) : dayjs()
    const start = base.startOf('week')
    const config = WEEK_RANGE_OPTIONS.find((o) => o.value === option)
    const days = config?.days || 14

    return {
        start: start.format('YYYY-MM-DD'),
        end: start.add(days - 1, 'day').format('YYYY-MM-DD'),
    }
}

/**
 * Navigate to previous/next period
 */
export const navigateDateRange = (
    current: TimelineDateRange,
    direction: 'prev' | 'next',
    weekRange: WeekRangeOption
): TimelineDateRange => {
    const config = WEEK_RANGE_OPTIONS.find((o) => o.value === weekRange)
    const days = config?.days || 14

    const currentStart = dayjs(current.start)
    const offset = direction === 'next' ? days : -days

    const newStart = currentStart.add(offset, 'day')
    const newEnd = newStart.add(days - 1, 'day')

    return {
        start: newStart.format('YYYY-MM-DD'),
        end: newEnd.format('YYYY-MM-DD'),
    }
}

/**
 * Format date range for display (e.g., "December 15 - December 28")
 */
export const formatDateRangeDisplay = (range: TimelineDateRange): string => {
    const start = dayjs(range.start)
    const end = dayjs(range.end)

    // Same month
    if (start.month() === end.month()) {
        return `${start.format('MMMM, D')} - ${end.format('D')}`
    }

    // Different months
    return `${start.format('MMMM, D')} - ${end.format('MMMM, D')}`
}

/**
 * Generate array of dates for timeline axis
 */
export const generateDateAxis = (range: TimelineDateRange): dayjs.Dayjs[] => {
    const dates: dayjs.Dayjs[] = []
    let current = dayjs(range.start)
    const end = dayjs(range.end)

    while (current.isBefore(end) || current.isSame(end, 'day')) {
        dates.push(current)
        current = current.add(1, 'day')
    }

    return dates
}

/**
 * Check if a date is today
 */
export const isToday = (date: dayjs.Dayjs): boolean => {
    return date.isSame(dayjs(), 'day')
}

/**
 * Check if a date is weekend
 */
export const isWeekend = (date: dayjs.Dayjs): boolean => {
    const day = date.day()
    return day === 0 || day === 6 // Sunday or Saturday
}

/**
 * Get day label for timeline header (e.g., "M", "T", "W")
 */
export const getDayLabel = (date: dayjs.Dayjs): string => {
    return date.format('dd').charAt(0).toUpperCase()
}

/**
 * Get day number for timeline header (e.g., "15", "16")
 */
export const getDayNumber = (date: dayjs.Dayjs): string => {
    return date.format('D')
}

/**
 * Jump to a specific date
 */
export const jumpToDate = (
    targetDate: string,
    weekRange: WeekRangeOption
): TimelineDateRange => {
    const target = dayjs(targetDate)
    const start = target.startOf('week')
    const config = WEEK_RANGE_OPTIONS.find((o) => o.value === weekRange)
    const days = config?.days || 14

    return {
        start: start.format('YYYY-MM-DD'),
        end: start.add(days - 1, 'day').format('YYYY-MM-DD'),
    }
}

/**
 * Get the current week's Monday
 */
export const getCurrentWeekStart = (): string => {
    return dayjs().startOf('week').format('YYYY-MM-DD')
}
